// ---------------------------------------------------------------------------
// C8: HQ Push Center — /admin/franchise/push
// ---------------------------------------------------------------------------
// Content selection (templates, pages, workflows). Push to locations.
// Status tracking. Override tracking. Parent org only.
// ---------------------------------------------------------------------------
import { useState } from "react";
import {
  Send,
  FileText,
  Layout,
  Workflow,
  Mail,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  MapPin,
  Shield,
  Plus,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { useHqPushData, useCreatePush, type PushRecord, type PushTarget, type PushableContent } from "@/hooks/use-franchise";
import { EmptyState } from "@/components/ui/empty-state";
import { formatDate, formatDateTime } from "@/lib/i18n";

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function PushSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 w-64 animate-pulse rounded bg-muted" />
      <div className="h-12 animate-pulse rounded-lg bg-muted" />
      <div className="space-y-4">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-40 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Content Type Icon
// ---------------------------------------------------------------------------

const CONTENT_ICONS: Record<string, typeof FileText> = {
  template: FileText,
  page: Layout,
  workflow: Workflow,
  email_sequence: Mail,
  form: FileText,
};

// ---------------------------------------------------------------------------
// Push Mode Badge
// ---------------------------------------------------------------------------

const MODE_CONFIG = {
  optional: { color: "bg-[var(--color-primary-light)] text-[var(--color-primary)]", icon: Clock, label: "Optional" },
  recommended: { color: "bg-[var(--color-warning)]/5 text-[var(--color-warning)]", icon: AlertTriangle, label: "Recommended" },
  mandatory: { color: "bg-[var(--color-destructive)]/5 text-[var(--color-destructive)]", icon: Shield, label: "Mandatory" },
} as const;

function ModeBadge({ mode }: { mode: "optional" | "recommended" | "mandatory" }) {
  const config = MODE_CONFIG[mode];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${config.color}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Target Status
// ---------------------------------------------------------------------------

function TargetStatus({ target }: { target: PushTarget }) {
  const statusConfig = {
    pending: { color: "text-[var(--color-warning)]", icon: Clock, label: "Pending" },
    accepted: { color: "text-[var(--color-success)]", icon: CheckCircle2, label: "Accepted" },
    rejected: { color: "text-[var(--color-destructive)]", icon: XCircle, label: "Rejected" },
    overridden: { color: "text-[var(--color-ai)]", icon: Shield, label: "Overridden" },
  } as const;

  const config = statusConfig[target.status];
  const Icon = config.icon;

  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/50 last:border-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-sm text-foreground">{target.locationName}</span>
        </div>
        <div className="mt-1 space-y-1 text-xs text-muted-foreground">
          <div>
            {target.pushedAt
              ? `Pushed ${formatDateTime(target.pushedAt)}`
              : "Push timestamp not recorded"}
          </div>
          <div>
            {target.respondedAt
              ? `Responded ${formatDateTime(target.respondedAt)}`
              : "No location response recorded yet"}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 ${config.color}`} />
        <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Push Record Card
// ---------------------------------------------------------------------------

function PushRecordCard({ record }: { record: PushRecord }) {
  const [expanded, setExpanded] = useState(false);
  const ContentIcon = CONTENT_ICONS[record.content.type] ?? FileText;

  const acceptedCount = record.targets.filter((t) => t.status === "accepted").length;
  const rejectedCount = record.targets.filter((t) => t.status === "rejected").length;
  const pendingCount = record.targets.filter((t) => t.status === "pending").length;
  const respondedCount = record.targets.filter((t) => t.respondedAt).length;

  const pushedMoments = record.targets
    .map((target) => target.pushedAt)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value));
  const respondedMoments = record.targets
    .map((target) => target.respondedAt)
    .filter((value): value is string => Boolean(value))
    .map((value) => new Date(value));

  const latestPushedAt = pushedMoments.length > 0
    ? pushedMoments.reduce((latest, current) => current > latest ? current : latest)
    : null;
  const latestRespondedAt = respondedMoments.length > 0
    ? respondedMoments.reduce((latest, current) => current > latest ? current : latest)
    : null;

  return (
    <div className="rounded-lg border border-border bg-background">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <ContentIcon className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground">{record.content.name}</span>
              <ModeBadge mode={record.mode} />
            </div>
            <span className="text-xs text-muted-foreground">
              Pushed by {record.createdBy} on {formatDate(record.createdAt)}
            </span>
            <p className="mt-1 text-sm text-muted-foreground">
              {record.content.description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-xs">
            {acceptedCount > 0 && (
              <span className="flex items-center gap-1 text-[var(--color-success)]">
                <CheckCircle2 className="h-3 w-3" />
                {acceptedCount}
              </span>
            )}
            {rejectedCount > 0 && (
              <span className="flex items-center gap-1 text-[var(--color-destructive)]">
                <XCircle className="h-3 w-3" />
                {rejectedCount}
              </span>
            )}
            {pendingCount > 0 && (
              <span className="flex items-center gap-1 text-[var(--color-warning)]">
                <Clock className="h-3 w-3" />
                {pendingCount}
              </span>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border">
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
              <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Content freshness</div>
              <div className="mt-1 text-sm font-medium text-foreground">
                Updated {formatDate(record.content.updatedAt)}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Mounted content metadata from the HQ tenant at push time.
              </div>
            </div>
            <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
              <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Latest push</div>
              <div className="mt-1 text-sm font-medium text-foreground">
                {latestPushedAt ? formatDateTime(latestPushedAt) : "Push time not recorded"}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                Latest mounted `pushedAt` timestamp across target locations.
              </div>
            </div>
            <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
              <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Location responses</div>
              <div className="mt-1 text-sm font-medium text-foreground">
                {respondedCount}/{record.targets.length} responded
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                {latestRespondedAt
                  ? `Latest response ${formatDateTime(latestRespondedAt)}`
                  : "No mounted response timestamps yet."}
              </div>
            </div>
          </div>

          <div className="mt-3">
            {record.targets.map((target) => (
              <TargetStatus key={target.locationId} target={target} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Push Form
// ---------------------------------------------------------------------------

function CreatePushForm({
  data,
  onSubmit,
  isSubmitting,
}: {
  data: { availableContent: PushableContent[]; locations: Array<{ id: string; name: string }> };
  onSubmit: (contentId: string, mode: "optional" | "recommended" | "mandatory", locationIds: string[]) => void;
  isSubmitting: boolean;
}) {
  const [selectedContent, setSelectedContent] = useState("");
  const [mode, setMode] = useState<"optional" | "recommended" | "mandatory">("optional");
  const [selectAll, setSelectAll] = useState(true);
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set(data.locations.map((l) => l.id)));
  const selectedContentRecord = data.availableContent.find((content) => content.id === selectedContent) ?? null;

  const toggleLocation = (id: string) => {
    const next = new Set(selectedLocations);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedLocations(next);
    setSelectAll(next.size === data.locations.length);
  };

  const toggleAll = () => {
    if (selectAll) {
      setSelectedLocations(new Set());
    } else {
      setSelectedLocations(new Set(data.locations.map((l) => l.id)));
    }
    setSelectAll(!selectAll);
  };

  return (
    <div className="rounded-lg border border-border bg-background p-4 space-y-4">
      <h3 className="font-medium text-foreground">New Push</h3>

      <div>
        <label className="block text-sm text-muted-foreground mb-1">Content</label>
        <select
          value={selectedContent}
          onChange={(e) => setSelectedContent(e.target.value)}
          className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        >
          <option value="">Select content to push...</option>
          {data.availableContent.map((c) => (
            <option key={c.id} value={c.id}>{c.name} ({c.type})</option>
          ))}
        </select>
        {selectedContentRecord ? (
          <div className="mt-3 rounded-lg border border-border/70 bg-muted/20 p-3">
            <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground/80">Selected content</div>
            <div className="mt-1 text-sm font-medium text-foreground">{selectedContentRecord.name}</div>
            <div className="mt-1 text-sm text-muted-foreground">{selectedContentRecord.description}</div>
            <div className="mt-2 text-xs text-muted-foreground">
              Updated {formatDate(selectedContentRecord.updatedAt)}
            </div>
          </div>
        ) : null}
      </div>

      <div>
        <label className="block text-sm text-muted-foreground mb-1">Push Mode</label>
        <div className="flex gap-2">
          {(["optional", "recommended", "mandatory"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium capitalize transition-colors ${
                mode === m
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)]"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm text-muted-foreground">Locations</label>
          <button onClick={toggleAll} className="text-xs text-[var(--color-primary)] hover:underline">
            {selectAll ? "Deselect All" : "Select All"}
          </button>
        </div>
        <div className="max-h-32 overflow-y-auto space-y-1 rounded-lg border border-border p-2">
          {data.locations.map((loc) => (
            <label key={loc.id} className="flex items-center gap-2 cursor-pointer py-0.5">
              <input
                type="checkbox"
                checked={selectedLocations.has(loc.id)}
                onChange={() => toggleLocation(loc.id)}
                className="rounded border-border text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
              />
              <span className="text-sm text-foreground">{loc.name}</span>
            </label>
          ))}
        </div>
      </div>

      <button
        onClick={() => onSubmit(selectedContent, mode, Array.from(selectedLocations))}
        disabled={isSubmitting || !selectedContent || selectedLocations.size === 0}
        className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 transition-colors disabled:opacity-50"
      >
        <Send className="h-4 w-4" />
        {isSubmitting ? "Pushing..." : "Push to Locations"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function HqPushCenter() {
  const { data, isLoading, error, refetch } = useHqPushData();
  const { mutate: createPush, isLoading: isCreating } = useCreatePush();
  const [showForm, setShowForm] = useState(false);

  const handlePush = async (contentId: string, mode: "optional" | "recommended" | "mandatory", locationIds: string[]) => {
    await createPush({ contentId, mode, locationIds });
    setShowForm(false);
    refetch();
  };

  if (isLoading) return <PushSkeleton />;

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load push center.
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState
        icon={<Send className="h-8 w-8" />}
        title="No content pushed yet"
        description="Push templates, pages, and workflows from HQ to your franchise locations."
        actionLabel="Create First Push"
        onAction={() => setShowForm(true)}
        variant="info"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">HQ Push Center</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Push
        </button>
      </div>

      {showForm && (
        <CreatePushForm
          data={{ availableContent: data.availableContent, locations: data.locations }}
          onSubmit={handlePush}
          isSubmitting={isCreating}
        />
      )}

      {/* Push History */}
      <div>
        <h2 className="text-lg font-medium text-foreground mb-3">Push History</h2>
        {data.pushHistory.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-8">No pushes recorded.</p>
        ) : (
          <div className="space-y-3">
            {data.pushHistory.map((record) => (
              <PushRecordCard key={record.id} record={record} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
